import React, { useRef, useState, useEffect } from 'react';
import { ChevronLeft } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Task } from '../../../types/workOrders';
import { saveOfflinePhoto, getOfflinePhotos, addPendingAction } from '../../../utils/offlineDb';
import Webcam from 'react-webcam';
import { PhotoMetadata } from './PhotoMetadata';
import { PhotoPreview } from './PhotoPreview';
import { OfflinePhotos } from './OfflinePhotos';
import { CameraView } from './CameraView';
import { TaskSelector } from './TaskSelector';
import { ActionButtons } from './ActionButtons';
import { usePhotoCapture } from './usePhotoCapture';

interface PhotoCaptureDialogProps {
  reportId: number;
  tasks: Task[];
  onBack: () => void;
  workOrder?: {
    wo_number: string;
  };
  onPhotoTaken: (file: File, taskItem: string) => Promise<void>;
}

export function PhotoCaptureDialog({ 
  reportId, 
  tasks, 
  workOrder,
  onBack,
  onPhotoTaken 
}: PhotoCaptureDialogProps) {
  const {
    selectedTask,
    setSelectedTask,
    loading,
    photos,
    offlinePhotos,
    showCamera,
    setShowCamera,
    facingMode,
    isFullscreen,
    cameraError,
    metadata,
    fileInputRef,
    webcamRef,
    handleFileSelect,
    handleCapture,
    handleCameraError,
    handleUploadPhotos,
    handleRemovePhoto,
    handleUserMedia,
    exitFullscreen,
    loadOfflinePhotos,
    isGettingLocation,
    locationError,
    lastPhoto,
    flashMode, 
    handleFlashToggle,
    hasFlash,
  } = usePhotoCapture({ reportId, onPhotoTaken, onBack });

  return (
    <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => {
              setShowCamera(false);
              exitFullscreen();
              onBack();
            }}
            className="p-2 hover:bg-gray-100 dark:hover:bg-dark-border rounded-lg transition-colors"
          >
            <ChevronLeft className="h-6 w-6 text-gray-600 dark:text-gray-400" />
          </button>
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              Take Photos
            </h2>
            {workOrder && (
              <p className="text-gray-600 dark:text-gray-400">WO #{workOrder.wo_number}</p>
            )}
          </div>
        </div>

        {/* Camera View */}
        {showCamera && (
          <CameraView
            isFullscreen={isFullscreen}
            facingMode={facingMode}
            cameraError={cameraError}
            selectedTask={selectedTask}
            lastPhoto={lastPhoto}
            flashMode={flashMode}
            hasFlash={hasFlash}
            onCapture={handleCapture}
            onExit={() => {
              setShowCamera(false);
              exitFullscreen();
            }}
            onUserMedia={handleUserMedia}
            onError={handleCameraError}
            onFlashToggle={handleFlashToggle}
            webcamRef={webcamRef}
          />
        )}

        <div className="space-y-6">
          {/* Task Selection */}
          <div className="bg-light-card dark:bg-dark-card rounded-lg p-6 border border-light-border dark:border-dark-border">
            <TaskSelector
              selectedTask={selectedTask}
              tasks={tasks}
              loading={loading}
              onChange={setSelectedTask}
            />
          </div>

          <PhotoPreview
            photos={photos}
            onRemove={handleRemovePhoto}
          />

          <PhotoMetadata
            latitude={metadata?.latitude || 0}
            longitude={metadata?.longitude || 0}
            accuracy={metadata?.accuracy} 
            isGettingLocation={isGettingLocation}
            locationError={locationError}
          />

          <OfflinePhotos
            photos={offlinePhotos}
            onPhotosUpdated={loadOfflinePhotos}
          />

          <div className="bg-light-card dark:bg-dark-card rounded-lg p-6 border border-light-border dark:border-dark-border space-y-4">
            <div className="relative w-full space-y-4">
              <input
                type="file"
                ref={fileInputRef}
                accept="image/*"
                multiple
                className="hidden"
                onChange={handleFileSelect}
                disabled={loading || !selectedTask}
              />
              <ActionButtons
                showCamera={showCamera}
                loading={loading}
                selectedTask={selectedTask}
                photosCount={photos.length}
                onChooseFiles={() => {
                  const input = document.createElement('input');
                  input.type = 'file';
                  input.accept = 'image/*';
                  input.multiple = true;
                  input.onchange = (event) => handleFileSelect(event as any);
                  input.click();
                }}
                onUseCamera={() => setShowCamera(true)}
                onStopCamera={() => {
                  setShowCamera(false);
                  exitFullscreen();
                }}
                onUpload={handleUploadPhotos}
              />
            </div>
          </div>
        </div>
      </div>
  );
}